import React, { useState } from 'react';
import { KeyRound, Key, Trash2 } from 'lucide-react';
import { toast } from 'sonner';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from '../ui/dialog';

const API_URL = process.env.REACT_APP_BACKEND_URL;
const headers = () => ({ Authorization: `Bearer ${localStorage.getItem('admin_token')}`, 'Content-Type': 'application/json' });

export const MemberRowActions = ({ member, currentAdminId, onChanged }) => {
  const [resetOpen, setResetOpen] = useState(false);
  const [pw, setPw] = useState('');
  const [busy, setBusy] = useState(false);

  const resetPassword = async () => {
    if (pw.length < 6) return toast.error('Passordet må være minst 6 tegn');
    setBusy(true);
    try {
      const res = await fetch(`${API_URL}/api/tennis/admin/members/${member.id}/password`, { method: 'PUT', headers: headers(), body: JSON.stringify({ new_password: pw }) });
      const data = await res.json();
      if (!res.ok) throw new Error(data.detail);
      toast.success(`Passord oppdatert for ${member.email}`);
      setResetOpen(false);
      setPw('');
    } catch (e) {
      toast.error(e.message || 'Kunne ikke oppdatere passord');
    } finally {
      setBusy(false);
    }
  };

  const deleteMember = async () => {
    if (!window.confirm(`Slett medlem "${member.first_name} ${member.last_name}"? Alle bookinger blir kansellert. Dette kan ikke angres.`)) return;
    setBusy(true);
    try {
      const res = await fetch(`${API_URL}/api/tennis/admin/members/${member.id}`, { method: 'DELETE', headers: headers() });
      const data = await res.json();
      if (!res.ok) throw new Error(data.detail);
      toast.success(data.message || 'Medlem slettet');
      onChanged();
    } catch (e) {
      toast.error(e.message || 'Kunne ikke slette medlem');
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="flex items-center gap-2 justify-end">
      <Button variant="outline" size="sm" data-testid={`member-reset-pw-${member.id}`} onClick={() => setResetOpen(true)} disabled={busy} className="border-slate-300 text-slate-700 hover:bg-slate-100" title="Sett nytt passord">
        <Key className="w-4 h-4" />
      </Button>
      {member.id !== currentAdminId && (
        <Button variant="outline" size="sm" data-testid={`member-delete-${member.id}`} onClick={deleteMember} disabled={busy} className="border-red-300 text-red-600 hover:bg-red-50" title="Slett medlem">
          <Trash2 className="w-4 h-4" />
        </Button>
      )}
      <Dialog open={resetOpen} onOpenChange={(o) => !o && setResetOpen(false)}>
        <DialogContent className="max-w-md bg-white text-slate-900" data-testid="reset-pw-dialog">
          <DialogHeader>
            <DialogTitle>Sett nytt passord</DialogTitle>
            <DialogDescription>Nytt passord for {member.first_name} {member.last_name} ({member.email}). Gi passordet til medlemmet på en trygg måte.</DialogDescription>
          </DialogHeader>
          <Input data-testid="reset-pw-input" type="text" placeholder="Nytt passord (minst 6 tegn)" value={pw} onChange={(e) => setPw(e.target.value)} />
          <DialogFooter>
            <Button data-testid="reset-pw-submit" onClick={resetPassword} disabled={busy} className="bg-[#CCFF00] text-black hover:bg-[#b8e600] font-bold">{busy ? 'Lagrer…' : 'Lagre passord'}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export const ChangeMyPasswordButton = () => {
  const [open, setOpen] = useState(false);
  const [current, setCurrent] = useState('');
  const [next, setNext] = useState('');
  const [busy, setBusy] = useState(false);

  const submit = async () => {
    if (next.length < 6) return toast.error('Nytt passord må være minst 6 tegn');
    setBusy(true);
    try {
      const res = await fetch(`${API_URL}/api/tennis/me/password`, { method: 'PUT', headers: headers(), body: JSON.stringify({ current_password: current, new_password: next }) });
      const data = await res.json();
      if (!res.ok) throw new Error(data.detail);
      toast.success('Ditt passord er oppdatert!');
      setOpen(false);
      setCurrent('');
      setNext('');
    } catch (e) {
      toast.error(e.message || 'Kunne ikke oppdatere passord');
    } finally {
      setBusy(false);
    }
  };

  return (
    <>
      <Button variant="outline" size="sm" data-testid="change-my-password-btn" onClick={() => setOpen(true)} className="border-green-500/50 text-green-600 hover:bg-green-500/10">
        <KeyRound className="w-4 h-4 mr-2" />
        Mitt passord
      </Button>
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-md bg-white text-slate-900" data-testid="my-pw-dialog">
          <DialogHeader>
            <DialogTitle>Endre mitt passord</DialogTitle>
            <DialogDescription>Skriv inn nåværende passord og velg et nytt (minst 6 tegn).</DialogDescription>
          </DialogHeader>
          <div className="space-y-3">
            <Input data-testid="my-pw-current" type="password" placeholder="Nåværende passord" value={current} onChange={(e) => setCurrent(e.target.value)} />
            <Input data-testid="my-pw-new" type="password" placeholder="Nytt passord" value={next} onChange={(e) => setNext(e.target.value)} />
          </div>
          <DialogFooter>
            <Button data-testid="my-pw-submit" onClick={submit} disabled={busy} className="bg-[#CCFF00] text-black hover:bg-[#b8e600] font-bold">{busy ? 'Lagrer…' : 'Oppdater passord'}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
};
